import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { TabView, TabPanel } from "primereact/tabview";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { InputText } from "primereact/inputtext";
import { AutoComplete } from "primereact/autocomplete";
import { Dialog } from "primereact/dialog";
import { SelectButton } from "primereact/selectbutton";
import ResponsiveView from "../../components/responsive/ResponsiveView";
import DataCard from "../../components/responsive/DataCard";
import CreditHistoryList from "../../components/payment/CreditHistoryList";
import { fetchAdminCreditStats, fetchAdminMethodStats, fetchAdminOtherPayments, fetchAdminPaymentHistory, fetchAdminStats, fetchCreditClients, formatMoney, paymentRowStatus, searchClients } from "../../services/clientPaymentService";
import { fetchAdminCashHistory } from "../../services/cashService";
import { CURRENCY_OPTIONS } from "../../services/exchangeRateService";
import styles from "./clientPayment.module.scss";

const METHOD_OPTIONS = [
    { label: "Cash", value: "cash" },
    { label: "Card", value: "card" },
    { label: "Wire Transfer", value: "wire" },
];

const SOURCE_OPTIONS = [
    { label: "Record Payment", value: "Record Payment" },
    { label: "Client & Other", value: "Client & Other" },
    { label: "Cash In", value: "Cash In" },
    { label: "Cash Out", value: "Cash Out" },
];

const personName = (p) => (p ? `${p.firstName || ""} ${p.lastName || ""}`.trim() || p.email : "Unknown");
const formatPair = (p) => {
    if (!p) return "-";
    const parts = [];
    if (p.usd) parts.push(formatMoney(p.usd, "usd"));
    if (p.jmd) parts.push(formatMoney(p.jmd, "jmd"));
    return parts.length ? parts.join(" + ") : formatMoney(0, "usd");
};

const AdminPaymentHistory = () => {
    const history = useHistory();

    // Shared filters - apply across every tab so a client's or a flight's
    // full payment picture (ledger, legacy, cash register, wire) lives in
    // one place instead of being scattered across separate pages.
    const [clientQuery, setClientQuery] = useState("");
    const [clientSuggestions, setClientSuggestions] = useState([]);
    const [selectedClient, setSelectedClient] = useState(null);
    const searchTimeout = useRef(null);
    const [flightNumber, setFlightNumber] = useState("");
    const [dateRange, setDateRange] = useState(null);

    const [stats, setStats] = useState(null);
    const [loadingStats, setLoadingStats] = useState(false);

    // Page-level "collected by method" overview - always visible, unlike
    // `stats` above which only appears once a client/flight is filtered.
    const [methodStats, setMethodStats] = useState(null);
    const [loadingMethodStats, setLoadingMethodStats] = useState(true);
    const [creditStats, setCreditStats] = useState(null);
    const [creditClients, setCreditClients] = useState([]);
    const [loadingCreditClients, setLoadingCreditClients] = useState(true);
    const [selectedCreditClient, setSelectedCreditClient] = useState(null);
    const [loadingCreditStats, setLoadingCreditStats] = useState(true);
    const [statsCurrency, setStatsCurrency] = useState("usd");

    const [paymentMethod, setPaymentMethod] = useState(null);
    const [payments, setPayments] = useState([]);
    const [loadingLedger, setLoadingLedger] = useState(true);
    const [detail, setDetail] = useState(null);

    const [otherPayments, setOtherPayments] = useState([]);
    const [loadingOther, setLoadingOther] = useState(true);

    const [cashEntries, setCashEntries] = useState([]);
    const [loadingCash, setLoadingCash] = useState(true);

    const dateFrom = dateRange && dateRange.length === 2 && dateRange[1] ? dateRange[0].toISOString() : null;
    const dateTo = dateRange && dateRange.length === 2 && dateRange[1] ? dateRange[1].toISOString() : null;

    const handleClientSearch = (e) => {
        const q = e.query;
        if (searchTimeout.current) clearTimeout(searchTimeout.current);
        if (!q || q.trim().length < 2) {
            setClientSuggestions([]);
            return;
        }
        searchTimeout.current = setTimeout(async () => {
            const res = await searchClients(q.trim());
            setClientSuggestions((res?.data || []).map((c) => ({ ...c, displayName: `${c.firstName} ${c.lastName} - ${c.email}` })));
        }, 300);
    };

    const clearClient = () => {
        setSelectedClient(null);
        setClientQuery("");
    };

    const loadStats = async () => {
        if (!selectedClient && !flightNumber) {
            setStats(null);
            return;
        }
        setLoadingStats(true);
        const res = await fetchAdminStats(selectedClient?._id, flightNumber);
        setStats(res?.success ? res.data : null);
        setLoadingStats(false);
    };

    const loadLedger = async () => {
        setLoadingLedger(true);
        const res = await fetchAdminPaymentHistory(selectedClient?._id, paymentMethod, dateFrom, dateTo, flightNumber);
        setPayments(res?.data || []);
        setLoadingLedger(false);
    };

    const loadOther = async () => {
        setLoadingOther(true);
        const res = await fetchAdminOtherPayments(selectedClient?._id, dateFrom, dateTo, flightNumber);
        setOtherPayments(res?.data || []);
        setLoadingOther(false);
    };

    const loadCashEntries = async () => {
        setLoadingCash(true);
        const res = await fetchAdminCashHistory(null, null, dateFrom, dateTo, selectedClient?._id, flightNumber);
        setCashEntries(Array.isArray(res?.data) ? res.data : res?.data?.entries || []);
        setLoadingCash(false);
    };

    const loadMethodStats = async () => {
        setLoadingMethodStats(true);
        const res = await fetchAdminMethodStats(selectedClient?._id, flightNumber, dateFrom, dateTo);
        setMethodStats(res?.success ? res.data : null);
        setLoadingMethodStats(false);
    };

    const loadAll = () => {
        loadStats();
        loadMethodStats();
        loadLedger();
        loadOther();
        loadCashEntries();
    };

    const loadCreditStats = async () => {
        setLoadingCreditStats(true);
        const res = await fetchAdminCreditStats();
        setCreditStats(res?.success ? res.data : null);
        setLoadingCreditStats(false);
    };

    const loadCreditClients = async () => {
        setLoadingCreditClients(true);
        const res = await fetchCreditClients();
        setCreditClients(res?.success ? res.data : []);
        setLoadingCreditClients(false);
    };

    useEffect(() => {
        loadAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Neither of these is scoped to a client/flight the way the rest of this
    // page's stats are - both are directory/aggregate views across every
    // client, so they're fetched once and never re-run when those filters
    // change.
    useEffect(() => {
        loadCreditStats();
        loadCreditClients();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Any shared filter change refreshes every tab plus the stats banner.
    useEffect(() => {
        loadAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedClient, flightNumber]);

    useEffect(() => {
        loadLedger();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paymentMethod]);

    const clientBody = (row) => personName(row.clientId) || row.clientName;
    const recordedByBody = (row) => `${personName(row.recordedBy) || row.recordedByName} (${row.recordedByRole})`;
    const dateBody = (row) => new Date(row.createdAt).toLocaleString();
    const methodBody = (row) => <span className={`${styles.badge} ${styles.badgeUntouched}`}>{row.paymentMethod}</span>;
    const viewBody = (row) => <Button icon="pi pi-eye" label="View" className="p-button-text p-button-sm" onClick={() => setDetail(row)} />;

    const otherDateBody = (row) => (row.updatedAt ? new Date(row.updatedAt).toLocaleString() : row.datePaid || "-");
    const otherMethodBody = (row) => <span className={`${styles.badge} ${styles.badgeUntouched}`}>{row.paymentMethod || "-"}</span>;
    const otherClientBody = (row) => personName(row.clientId) || row.clientName;
    const otherOwnerBody = (row) => row.paymentOwner || "-";

    const cashStaffBody = (row) => personName(row.staff);
    const cashInvoiceBody = (row) => row.invoiceId?.invoiceId || row.invoiceId?.Waybills || "-";
    // A "$" symbol alone can't tell an admin whether a USD-tagged entry is
    // genuinely USD or the currency dimension just isn't wired up - this
    // makes it explicit and visible on every row, not just J$ ones.
    const currencyBody = (row) => {
        const currency = (row.currency || "usd").toLowerCase();
        return <span className={`${styles.badge} ${currency === "jmd" ? styles.badgePartial : styles.badgeUntouched}`}>{currency.toUpperCase()}</span>;
    };

    const creditClientCard = (row) => (
        <DataCard
            key={row._id}
            title={`${row.firstName || ""} ${row.lastName || ""}`.trim()}
            subtitle={row.email}
            badge={<span className={`${styles.badge} ${styles.badgeUntouched}`}>{row.role}</span>}
            fields={[{ label: "Credit Balance", value: formatMoney(row.accountCreditBalance) }]}
            onClick={() => setSelectedCreditClient(row)}
        />
    );

    const paymentCard = (row) => (
        <DataCard
            key={row._id || row.createdAt}
            title={formatMoney(row.amountReceived, row.currency)}
            subtitle={clientBody(row)}
            badge={methodBody(row)}
            fields={[
                { label: "Date", value: dateBody(row) },
                { label: "Currency", value: currencyBody(row) },
                { label: "Mode", value: row.mode === "selective" ? "Selective" : "Automatic" },
                { label: "Credit Applied", value: formatMoney(row.creditApplied) },
                { label: "Total Allocated", value: formatMoney(row.totalAllocated) },
                { label: "Credit Added", value: formatMoney(row.creditAdded) },
                { label: "Recorded By", value: recordedByBody(row) },
            ]}
            actions={<Button icon="pi pi-eye" label="View Allocation" className="p-button-text p-button-sm" onClick={() => setDetail(row)} />}
        />
    );

    const otherPaymentCard = (row) => (
        <DataCard
            key={row._id || row.invoiceId}
            title={formatMoney(row.paidAmount)}
            subtitle={otherClientBody(row)}
            badge={otherMethodBody(row)}
            fields={[
                { label: "Date", value: otherDateBody(row) },
                { label: "Invoice", value: row.invoiceId || row.invoiceNo },
                { label: "Waybill", value: row.Waybills },
                { label: "Flight #", value: row.shipmentNumber },
                { label: "Remaining", value: formatMoney(row.invoiceAmount) },
                { label: "Received By", value: otherOwnerBody(row) },
            ]}
        />
    );

    // Once a client and/or flight is filtered, the three event-level sources
    // (ledger, legacy/self-service, cash register) merge into one feed so
    // that client's whole payment picture reads chronologically in one
    // place instead of being split across tabs.
    const isFiltered = !!(selectedClient || flightNumber);
    const [sourceFilter, setSourceFilter] = useState(null);

    const combinedRows = useMemo(() => {
        const rows = [];
        payments.forEach((p) => {
            rows.push({
                _key: `ledger-${p._id}`,
                source: "Record Payment",
                date: p.createdAt,
                client: clientBody(p),
                amount: p.amountReceived,
                currency: p.currency,
                method: p.paymentMethod,
                reference: `${p.allocations?.length || 0} invoice(s)`,
                onView: () => setDetail(p),
            });
        });
        otherPayments.forEach((o) => {
            rows.push({
                _key: `other-${o._id}`,
                source: "Client & Other",
                date: o.updatedAt || o.datePaid,
                client: otherClientBody(o),
                amount: o.paidAmount,
                method: o.paymentMethod || "-",
                reference: o.invoiceId || o.invoiceNo || o.Waybills || "-",
            });
        });
        cashEntries.forEach((c) => {
            rows.push({
                _key: `cash-${c._id}`,
                source: c.type === "out" ? "Cash Out" : "Cash In",
                date: c.createdAt,
                client: cashStaffBody(c),
                amount: c.type === "out" ? -c.amount : c.amount,
                currency: c.currency,
                method: "cash",
                reference: cashInvoiceBody(c),
            });
        });
        return rows.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [payments, otherPayments, cashEntries]);

    const filteredCombinedRows = sourceFilter ? combinedRows.filter((r) => r.source === sourceFilter) : combinedRows;

    const combinedSourceBadge = (row) => {
        const badgeClass = row.source === "Cash Out" ? "badgePartial" : row.source === "Record Payment" ? "badgePaid" : "badgeUntouched";
        return <span className={`${styles.badge} ${styles[badgeClass]}`}>{row.source}</span>;
    };

    const combinedCard = (row) => (
        <DataCard
            key={row._key}
            title={formatMoney(row.amount, row.currency)}
            subtitle={row.client}
            badge={combinedSourceBadge(row)}
            fields={[
                { label: "Date", value: row.date ? new Date(row.date).toLocaleString() : "-" },
                { label: "Method", value: row.method },
                { label: "Currency", value: currencyBody(row) },
                { label: "Reference", value: row.reference },
            ]}
            actions={row.onView && <Button icon="pi pi-eye" label="View Allocation" className="p-button-text p-button-sm" onClick={row.onView} />}
        />
    );

    const allocationCard = (row, i) => {
        const { label, badgeClass } = paymentRowStatus(row);
        return (
            <DataCard
                key={row.invoiceId || i}
                title={row.invoiceId || row.invoiceNo || "-"}
                subtitle={row.Waybills}
                badge={<span className={`${styles.badge} ${styles[badgeClass]}`}>{label}</span>}
                fields={[
                    { label: "Flight #", value: row.shipmentNumber },
                    { label: "Balance Before", value: formatMoney(row.balanceBefore) },
                    { label: "Applied", value: formatMoney(row.amountApplied) },
                    { label: "Balance After", value: formatMoney(row.balanceAfter) },
                ]}
            />
        );
    };

    return (
        <div className={styles.page}>
            <div className={styles.backLink} onClick={() => history.push("/invoices")}>
                <i className="pi pi-arrow-left" /> Back to Invoices
            </div>

            <div className={styles.pageHeader}>
                <div className={styles.headerTitleRow}>
                    <div className={styles.headerIcon}>
                        <i className="pi pi-history" />
                    </div>
                    <div>
                        <h2>Payment History</h2>
                        <p className={styles.subtitle}>Every client payment - ledger, legacy, cash register, and wire - in one place</p>
                    </div>
                </div>
            </div>

            <div className={styles.filtersSection}>
                <div className={styles.filterGroup} style={{ minWidth: "260px" }}>
                    <label>Client</label>
                    {selectedClient ? (
                        <div className={styles.applyRow}>
                            <span className={`${styles.badge} ${styles.badgeUntouched}`} style={{ padding: "8px 14px" }}>
                                {personName(selectedClient)}
                            </span>
                            <Button icon="pi pi-times" className="p-button-text p-button-sm" onClick={clearClient} />
                        </div>
                    ) : (
                        <AutoComplete
                            value={clientQuery}
                            suggestions={clientSuggestions}
                            completeMethod={handleClientSearch}
                            field="displayName"
                            onChange={(e) => setClientQuery(typeof e.value === "string" ? e.value : e.value.displayName)}
                            onSelect={(e) => {
                                setSelectedClient(e.value);
                                setClientQuery("");
                            }}
                            placeholder="Search by client name or email..."
                            inputStyle={{ width: "240px" }}
                        />
                    )}
                </div>
                <div className={styles.filterGroup}>
                    <label htmlFor="flightNumber">Flight #</label>
                    <InputText id="flightNumber" value={flightNumber} onChange={(e) => setFlightNumber(e.target.value)} placeholder="e.g. AA1234" style={{ width: "150px" }} />
                </div>
                <div className={styles.filterGroup}>
                    <label>Date Range</label>
                    <Calendar value={dateRange} onChange={(e) => setDateRange(e.value)} selectionMode="range" readOnlyInput placeholder="Select date range" />
                </div>
                <div className={styles.filterGroup}>
                    <Button label="Apply" icon="pi pi-search" onClick={loadAll} className={styles.primaryButton} />
                </div>
            </div>

            <div className={styles.sectionCard} style={{ maxWidth: "none" }}>
                <div className={styles.sectionCardHeader}>
                    <h3>
                        <i className="pi pi-chart-pie" />
                        Collected by Method{selectedClient || flightNumber ? " (current filter)" : ""}
                    </h3>
                    <div className={styles.filterGroup} style={{ margin: 0 }}>
                        <label>Show In</label>
                        <SelectButton value={statsCurrency} options={CURRENCY_OPTIONS} onChange={(e) => e.value && setStatsCurrency(e.value)} />
                    </div>
                </div>
                {loadingMethodStats ? (
                    <div className={styles.emptyBreaks}>Loading method totals...</div>
                ) : !methodStats ? (
                    <div className={styles.emptyBreaks}>Unable to load method totals.</div>
                ) : (
                    <>
                        {statsCurrency === "jmd" && !methodStats.currentRate && (
                            <p style={{ fontSize: "12px", color: "#b45309", marginTop: 0 }}>
                                No exchange rate is set - JMD totals can't be estimated. Set one from Cash &gt; Exchange Rate.
                            </p>
                        )}
                        <div className={styles.statsRow}>
                            {[
                                { key: "cash", label: "Cash", icon: "pi pi-wallet" },
                                { key: "wire", label: "Wire", icon: "pi pi-send" },
                                { key: "card", label: "Card", icon: "pi pi-credit-card" },
                                { key: "amazonPay", label: "Amazon Pay", icon: "pi pi-amazon" },
                            ].map(({ key, label, icon }) => {
                                const m = methodStats[key];
                                const headline =
                                    statsCurrency === "usd"
                                        ? formatMoney(m.usdEquivalentTotal, "usd")
                                        : methodStats.currentRate
                                          ? formatMoney(m.usdEquivalentTotal * methodStats.currentRate, "jmd")
                                          : "-";
                                return (
                                    <div className={styles.statCard} key={key}>
                                        <div className={styles.statIcon}>
                                            <i className={icon} />
                                        </div>
                                        <div className={styles.statText}>
                                            <div className={styles.statValue}>{headline}</div>
                                            <div className={styles.statLabel}>{label}</div>
                                        </div>
                                    </div>
                                );
                            })}
                            <div className={styles.statCard}>
                                <div className={`${styles.statIcon} ${styles.iconCredit}`}>
                                    <i className="pi pi-money-bill" />
                                </div>
                                <div className={styles.statText}>
                                    <div className={styles.statValue}>
                                        {statsCurrency === "usd"
                                            ? formatMoney(methodStats.total.usdEquivalentTotal, "usd")
                                            : methodStats.currentRate
                                              ? formatMoney(methodStats.total.usdEquivalentTotal * methodStats.currentRate, "jmd")
                                              : "-"}
                                    </div>
                                    <div className={styles.statLabel}>Total Collected</div>
                                </div>
                            </div>
                            <div className={styles.statCard}>
                                <div className={`${styles.statIcon} ${styles.iconCredit}`}>
                                    <i className="pi pi-star" />
                                </div>
                                <div className={styles.statText}>
                                    <div className={styles.statValue}>{loadingCreditStats ? "..." : formatMoney(creditStats?.totalOutstandingCredit || 0)}</div>
                                    <div className={styles.statLabel}>Total Outstanding Client Credit</div>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>

            {(selectedClient || flightNumber) && (
                <div className={styles.statsRow}>
                    {loadingStats ? (
                        <div className={styles.statCard}>
                            <div className={styles.statText}>
                                <div className={styles.statLabel}>Loading stats...</div>
                            </div>
                        </div>
                    ) : (
                        stats && (
                            <>
                                <div className={styles.statCard}>
                                    <div className={styles.statIcon}>
                                        <i className="pi pi-file" />
                                    </div>
                                    <div className={styles.statText}>
                                        <div className={styles.statValue}>{stats.invoiceCount}</div>
                                        <div className={styles.statLabel}>Invoices</div>
                                    </div>
                                </div>
                                <div className={styles.statCard}>
                                    <div className={styles.statIcon}>
                                        <i className="pi pi-wallet" />
                                    </div>
                                    <div className={styles.statText}>
                                        <div className={styles.statValue}>{formatMoney(stats.totalOutstanding)}</div>
                                        <div className={styles.statLabel}>Outstanding</div>
                                    </div>
                                </div>
                                <div className={styles.statCard}>
                                    <div className={styles.statIcon}>
                                        <i className="pi pi-check-circle" />
                                    </div>
                                    <div className={styles.statText}>
                                        <div className={styles.statValue}>{formatMoney(stats.totalPaidOnInvoices)}</div>
                                        <div className={styles.statLabel}>Total Paid</div>
                                    </div>
                                </div>
                                <div className={styles.statCard}>
                                    <div className={styles.statIcon}>
                                        <i className="pi pi-book" />
                                    </div>
                                    <div className={styles.statText}>
                                        <div className={styles.statValue} style={{ fontSize: "16px" }}>{formatPair(stats.ledgerTotalReceived)}</div>
                                        <div className={styles.statLabel}>Record Payment</div>
                                        <div className={styles.statSubtext}>{stats.ledgerPaymentCount} payment(s)</div>
                                    </div>
                                </div>
                                <div className={styles.statCard}>
                                    <div className={styles.statIcon}>
                                        <i className="pi pi-arrow-down" />
                                    </div>
                                    <div className={styles.statText}>
                                        <div className={styles.statValue} style={{ fontSize: "16px" }}>{formatPair(stats.cashIn)}</div>
                                        <div className={styles.statLabel}>Cash In</div>
                                    </div>
                                </div>
                                <div className={styles.statCard}>
                                    <div className={styles.statIcon}>
                                        <i className="pi pi-arrow-up" />
                                    </div>
                                    <div className={styles.statText}>
                                        <div className={styles.statValue} style={{ fontSize: "16px" }}>{formatPair(stats.cashOut)}</div>
                                        <div className={styles.statLabel}>Cash Out</div>
                                    </div>
                                </div>
                                <div className={styles.statCard}>
                                    <div className={styles.statIcon}>
                                        <i className="pi pi-money-bill" />
                                    </div>
                                    <div className={styles.statText}>
                                        <div className={styles.statValue} style={{ fontSize: "16px" }}>{formatPair(stats.cashNet)}</div>
                                        <div className={styles.statLabel}>Cash Net</div>
                                    </div>
                                </div>
                                <div className={styles.statCard}>
                                    <div className={styles.statIcon}>
                                        <i className="pi pi-send" />
                                    </div>
                                    <div className={styles.statText}>
                                        <div className={styles.statValue}>{formatMoney(stats.wireTotal)}</div>
                                        <div className={styles.statLabel}>Wire Total</div>
                                    </div>
                                </div>
                                {stats.accountCreditBalance !== null && (
                                    <div className={styles.statCard}>
                                        <div className={`${styles.statIcon} ${styles.iconCredit}`}>
                                            <i className="pi pi-star" />
                                        </div>
                                        <div className={styles.statText}>
                                            <div className={styles.statValue}>{formatMoney(stats.accountCreditBalance)}</div>
                                            <div className={styles.statLabel}>Credit Balance</div>
                                        </div>
                                    </div>
                                )}
                            </>
                        )
                    )}
                </div>
            )}

            {isFiltered ? (
                <div className={styles.sectionCard} style={{ maxWidth: "100%" }}>
                    <div className={styles.sectionCardHeader}>
                        <h3>
                            <i className="pi pi-list" />
                            All Payment Activity ({filteredCombinedRows.length})
                        </h3>
                    </div>
                    <p style={{ fontSize: "13px", color: "#64748b", marginTop: 0 }}>
                        Every recorded payment, legacy/self-service payment, and cash register entry for this filter, newest first.
                    </p>
                    <div className={styles.filtersSection}>
                        <div className={styles.filterGroup}>
                            <label>Source</label>
                            <Dropdown value={sourceFilter} options={SOURCE_OPTIONS} onChange={(e) => setSourceFilter(e.value)} placeholder="All Sources" showClear style={{ minWidth: "190px" }} />
                        </div>
                    </div>
                    <ResponsiveView
                        value={filteredCombinedRows}
                        loading={loadingLedger || loadingOther || loadingCash}
                        loadingText="Loading payment activity..."
                        emptyMessage="No payment activity found for this filter."
                        storageKey="paymentHistory.combined"
                        renderCard={combinedCard}
                    >
                        <DataTable value={filteredCombinedRows} paginator rows={20} rowsPerPageOptions={[20, 50, 100]} className="datatable-responsive custom-datatable" emptyMessage="No payment activity found for this filter.">
                            <Column header="Date" body={(row) => (row.date ? new Date(row.date).toLocaleString() : "-")} />
                            <Column header="Source" body={combinedSourceBadge} />
                            <Column header="Client / Staff" field="client" />
                            <Column header="Amount" body={(row) => formatMoney(row.amount, row.currency)} />
                            <Column header="Currency" body={currencyBody} />
                            <Column header="Method" field="method" />
                            <Column header="Reference" field="reference" />
                            <Column header="" body={(row) => (row.onView ? <Button icon="pi pi-eye" label="View" className="p-button-text p-button-sm" onClick={row.onView} /> : null)} style={{ width: "100px" }} />
                        </DataTable>
                    </ResponsiveView>
                </div>
            ) : (
                <TabView>
                <TabPanel header={`Staff Recorded (${payments.length})`} leftIcon="pi pi-wallet">
                    <div className={styles.filtersSection}>
                        <div className={styles.filterGroup}>
                            <label>Payment Method</label>
                            <Dropdown value={paymentMethod} options={METHOD_OPTIONS} onChange={(e) => setPaymentMethod(e.value)} placeholder="All Methods" showClear style={{ minWidth: "180px" }} />
                        </div>
                    </div>
                    <ResponsiveView value={payments} loading={loadingLedger} loadingText="Loading payment history..." emptyMessage="No payments recorded yet." storageKey="paymentHistory.staff" renderCard={paymentCard}>
                        <DataTable value={payments} paginator rows={20} rowsPerPageOptions={[20, 50, 100]} className="datatable-responsive custom-datatable" emptyMessage="No payments recorded yet.">
                            <Column header="Date" body={dateBody} />
                            <Column header="Client" body={clientBody} />
                            <Column header="Mode" body={(row) => (row.mode === "selective" ? "Selective" : "Automatic")} />
                            <Column header="Method" body={methodBody} />
                            <Column header="Amount Received" body={(row) => formatMoney(row.amountReceived, row.currency)} />
                            <Column header="Currency" body={currencyBody} />
                            <Column header="Credit Applied" body={(row) => formatMoney(row.creditApplied)} />
                            <Column header="Total Allocated" body={(row) => formatMoney(row.totalAllocated)} />
                            <Column header="Credit Added" body={(row) => formatMoney(row.creditAdded)} />
                            <Column header="Recorded By" body={recordedByBody} />
                            <Column header="" body={viewBody} style={{ width: "100px" }} />
                        </DataTable>
                    </ResponsiveView>
                </TabPanel>

                <TabPanel header={`Client & Other Payments (${otherPayments.length})`} leftIcon="pi pi-credit-card">
                    <p style={{ fontSize: "13px", color: "#64748b", marginTop: 0 }}>
                        Payments clients made themselves from their own account (card, PayPal, Amazon Pay), plus anything recorded through the older single/bulk mark-paid actions. These
                        weren't logged invoice-by-invoice like the flow above, so each row is that invoice's latest payment snapshot rather than a full breakdown.
                    </p>
                    <ResponsiveView value={otherPayments} loading={loadingOther} loadingText="Loading other payments..." emptyMessage="No other payments found." storageKey="paymentHistory.other" renderCard={otherPaymentCard}>
                        <DataTable value={otherPayments} paginator rows={20} rowsPerPageOptions={[20, 50, 100]} className="datatable-responsive custom-datatable" emptyMessage="No other payments found.">
                            <Column header="Date" body={otherDateBody} />
                            <Column header="Client" body={otherClientBody} />
                            <Column field="invoiceId" header="Invoice" body={(row) => row.invoiceId || row.invoiceNo || "-"} />
                            <Column field="Waybills" header="Waybill" />
                            <Column field="shipmentNumber" header="Flight #" body={(row) => row.shipmentNumber || "-"} />
                            <Column header="Method" body={otherMethodBody} />
                            <Column header="Paid Amount" body={(row) => formatMoney(row.paidAmount)} />
                            <Column header="Remaining" body={(row) => formatMoney(row.invoiceAmount)} />
                            <Column header="Received By" body={otherOwnerBody} />
                        </DataTable>
                    </ResponsiveView>
                </TabPanel>

                <TabPanel header={`Client Credit (${creditClients.length})`} leftIcon="pi pi-star">
                    <p style={{ fontSize: "13px", color: "#64748b", marginTop: 0 }}>
                        Every client currently holding account credit, highest balance first. Click a row to see the full history behind that balance.
                    </p>
                    <ResponsiveView
                        value={creditClients}
                        loading={loadingCreditClients}
                        loadingText="Loading client credit..."
                        emptyMessage="No clients currently hold account credit."
                        storageKey="paymentHistory.creditClients"
                        renderCard={creditClientCard}
                    >
                        <DataTable
                            value={creditClients}
                            paginator
                            rows={20}
                            rowsPerPageOptions={[20, 50, 100]}
                            className="datatable-responsive custom-datatable"
                            emptyMessage="No clients currently hold account credit."
                        >
                            <Column header="Client" body={(row) => `${row.firstName || ""} ${row.lastName || ""}`.trim()} />
                            <Column field="email" header="Email" />
                            <Column field="role" header="Role" />
                            <Column header="Credit Balance" body={(row) => formatMoney(row.accountCreditBalance)} />
                            <Column header="" body={(row) => <Button icon="pi pi-eye" label="History" className="p-button-text p-button-sm" onClick={() => setSelectedCreditClient(row)} />} style={{ width: "120px" }} />
                        </DataTable>
                    </ResponsiveView>
                </TabPanel>
                </TabView>
            )}

            <Dialog
                header={selectedCreditClient ? `Credit History - ${selectedCreditClient.firstName || ""} ${selectedCreditClient.lastName || ""}`.trim() : "Credit History"}
                visible={!!selectedCreditClient}
                style={{ width: "min(650px, 95vw)" }}
                onHide={() => setSelectedCreditClient(null)}
            >
                {selectedCreditClient && (
                    <div>
                        <p style={{ marginTop: 0, fontSize: "13px", color: "#64748b" }}>
                            {selectedCreditClient.email} &middot; Current balance: <strong>{formatMoney(selectedCreditClient.accountCreditBalance)}</strong>
                        </p>
                        <CreditHistoryList clientId={selectedCreditClient._id} />
                    </div>
                )}
            </Dialog>

            <Dialog header="Payment Allocation Detail" visible={!!detail} style={{ width: "min(700px, 95vw)" }} onHide={() => setDetail(null)}>
                {detail && (
                    <div>
                        <p>
                            <strong>{clientBody(detail)}</strong> paid {formatMoney(detail.amountReceived, detail.currency)} via {detail.paymentMethod}
                            {detail.creditApplied > 0 && ` (plus ${formatMoney(detail.creditApplied)} applied from existing credit)`} on {new Date(detail.createdAt).toLocaleString()}.
                        </p>
                        <ResponsiveView value={detail.allocations} emptyMessage="No allocations recorded." storageKey="paymentHistory.allocations" renderCard={allocationCard} flush>
                            <DataTable value={detail.allocations} className="datatable-responsive custom-datatable">
                                <Column field="invoiceId" header="Invoice" body={(row) => row.invoiceId || row.invoiceNo || "-"} />
                                <Column field="Waybills" header="Waybill" />
                                <Column field="shipmentNumber" header="Flight #" body={(row) => row.shipmentNumber || "-"} />
                                <Column header="Balance Before" body={(row) => formatMoney(row.balanceBefore)} />
                                <Column header="Applied" body={(row) => formatMoney(row.amountApplied)} />
                                <Column header="Balance After" body={(row) => formatMoney(row.balanceAfter)} />
                                <Column
                                    header="Status"
                                    body={(row) => {
                                        const { label, badgeClass } = paymentRowStatus(row);
                                        return <span className={`${styles.badge} ${styles[badgeClass]}`}>{label}</span>;
                                    }}
                                />
                            </DataTable>
                        </ResponsiveView>
                        {detail.creditAdded > 0 && <p style={{ marginTop: "14px" }}>Overpayment of {formatMoney(detail.creditAdded)} was added to the client's credit balance.</p>}
                    </div>
                )}
            </Dialog>
        </div>
    );
};

export default AdminPaymentHistory;
